import { Attrs, EventListeners, MinzeElement } from 'minze'
import { Font, SCREEN_WIDTHS } from '../../constants'
import { Callback } from '../../modules/callback'
import { Locale } from '../../modules/locale'
import { getVisibleWidth } from '../../modules/utils'

Locale.getInstance().registerKeySet('fr', 'radiusFilter', {
  label: 'Radius',
})

Locale.getInstance().registerKeySet('en', 'radiusFilter', {
  label: 'Radius',
})

export interface LvcRadiusProfile {
  dataGroupId?: string
  dataCallback?: string
  defaultSelectedRadius?: string
}

export class LvcRadiusProfile extends MinzeElement {
  private clickCb: string = ''
  private clearCb: string = ''
  private filterOptions = [2, 5, 10, 30, 50, 100]
  private selectedRadius: number = 10 // default radius in km

  // These properties come from attributes
  public defaultSelectedRadius?: string
  public preferredRadius?: number

  attrs: Attrs = [['data-callback'], ['default-selected-radius', 10]]

  static observedAttributes = ['default-selected-radius', 'preferred-radius']

  onAttributeChange(
    name?: string,
    oldValue?: string | null,
    newValue?: string | null
  ): Promise<void> | void {
    if (name === 'default-selected-radius' && newValue) {
      this.defaultSelectedRadius = newValue
      this.selectedRadius = parseInt(newValue)
    }
    this.rerender()
  }

  onSliderInputChanged = (event: Event) => {
    const target = event.target as HTMLInputElement
    const index = Number(target.value)
    const radius = this.filterOptions[index]
    this.selectedRadius = radius
    Callback.call(this.dataCallback as string, this.selectedRadius)
    this.rerender()
  }

  getRadiusValue = () => {
    if (this.dataCallback) {
      return this.selectedRadius
    }
    if (this.defaultSelectedRadius) {
      return this.filterOptions.indexOf(parseInt(this.defaultSelectedRadius))
    }
    return this.filterOptions.indexOf(this.selectedRadius)
  }

  renderRadiusFilter() {
    const sliderMin = 0
    const sliderMax = this.filterOptions.length - 1
    return /*html*/ `
      <div class="slider-container">
        <div class="slider-labels">
          ${this.filterOptions
            .map(
              (option, index) => `
            <span class="slider-label" style="left: ${(index / sliderMax) * 100}%;">
              ${option} km
            </span>
          `
            )
            .join('')}
        </div>
        <input
          type="range"
          min="${sliderMin}"
          max="${sliderMax}"
          value="${this.filterOptions.indexOf(
            this.defaultSelectedRadius ? parseInt(this.defaultSelectedRadius) : this.selectedRadius
          )}"
          class="slider"
          id="radiusSlider"
          step="1"
        />
      </div>
    `
  }

  html = () => {
    if (getVisibleWidth() < SCREEN_WIDTHS.DESKTOP_MEDIUM) {
      return this.renderRadiusFilter()
    } else {
      return this.renderRadiusFilter()
    }
  }

  css = () => `

    :host {
      width: 100%;
    }
    .slider-container {
      margin-top: 1rem;
      width: 100%;
    }

    .slider {
      width: 100%;
      margin-top: 2rem;
      -webkit-appearance: none;
      appearance: none;
      height: 8px;
      background: #e0e0e0; 
      border-radius: 4px; 
      outline: none;
      position: relative; 
    }

    .slider-labels {
      position: relative;
      display: flex;
      justify-content: space-between;
      width: 100%;
    }
    .slider-label {
      position: absolute;
      transform: translateX(-50%);
      font-size: 12px;
      color: #1E007D;
      white-space: nowrap;
    }

    .slider::-webkit-slider-thumb {
      -webkit-appearance: none; 
      appearance: none; 
      width: 20px;
      height: 20px; 
      background: #1e007d; 
      border-radius: 50%;
      cursor: pointer; 
      box-shadow: 0 0 2px rgba(0, 0, 0, 0.5); 
      position: relative;
      top: 50%; 
      transform: translateY(-50%); 
      transition: background 0.3s, transform 0.2s; 
    }

    .slider::-moz-range-thumb {
      width: 20px;
      height: 20px;
      background: #1e007d;
      border: none;
      border-radius: 50%;
      cursor: pointer;
      box-shadow: 0 0 2px rgba(0, 0, 0, 0.5);
      position: relative;
      transform: translateY(-50%); 
    }

    .slider::-ms-thumb {
      width: 20px;
      height: 20px;
      background: #1e007d;
      border: none;
      border-radius: 50%;
      cursor: pointer;
      box-shadow: 0 0 2px rgba(0, 0, 0, 0.5);
      position: relative;
      top: 50%; 
      transform: translateY(-50%); 
    }

    .slider::-webkit-slider-runnable-track {
      width: 100%;
      height: 8px;
      background: #e0e0e0; 
      border-radius: 4px;
    }

    .slider:focus::-webkit-slider-runnable-track {
      background: #cfcfcf;
    }

    .slider-label:first-child {
      transform: translateX(-10%);
    }
    .slider-label:last-child {
      transform: translateX(-90%);
    }
    .dropdown-content-wrapper {
      display: flex;
      flex-direction: column;
      gap: 15px;
      padding: 8px;
    }
    lvc-dropdown {
      width: 100%;
    }
    lvc-dropdown::part(dropdown-content) {
      top: 3px;
      min-width: auto;
    }
    h4 {
      margin: 0 0 4px 0;
      font-weight: ${Font.weight.medium};
      font-size: 14px;
      line-height: 150%;
      opacity: 0.3;
    }
    
    .header {
      padding-bottom: 20px;
      margin-bottom: 20px;
      border-bottom: 1px solid #1E007D4D;
    }

    .disciplines-subtitle {
      font-weight: ${Font.weight.bold};
      font-size: 16px;
      line-height: 150%;
      color: #1E007D;
      opacity: 1;
      display: flex;
      align-items: center;
      width: fit-content;
      position: relative;
    }
    
    .disciplines-subtitle .counter {
      border: 1px solid #1E007D;
      width: 30px;
      height: 30px;
      font-size: 12px;
      font-weight: 400;
      border-radius: 15px;
      display: flex;
      align-items: center;
      justify-content: center;
      position: absolute;
      right: -6px;
      bottom: 6px;
      transform: translateX(100%);
    }

    lvc-drawer .audience-dropdown-content {
      background: transparent;
      box-shadow: none;
      border: none;
      width: auto;
      margin: auto;
    }

    lvc-drawer .disciplines-subtitle {
      display: none;
    }

    @media screen and (max-width: 730px) {
      #disciplines-content {
        max-height: calc(100vh - 72px - 73px - 84.5px - 10px);
        max-width: 100vw;
        overflow-y: scroll;
      }
    }
  `

  eventListeners: EventListeners = [['#radiusSlider', 'input', this.onSliderInputChanged]]
}
